// The shared toolbar look for every pane header (#11548 r60): one size, one
// resting color, one hover — the Document pane's style, adopted everywhere so
// no pane reads greyed-out or oversized beside another.
//
// The ink reads the design tokens (#12481): ink-3 at rest, the hover wash and
// full ink on hover, the same values every RMS quiet control uses. It is its
// own token because the size is not universal: the zoom group packs three
// smaller controls into one h-8 box, and the non-compact mode toggle is h-10.
// Those need the same color without the same geometry, and hand-copying it is
// exactly how Details and the mode toggle drifted onto `text-gray-600` while
// their neighbours stayed on the shared ink (#11852).

/** Geometry only. Carries no color, so an ink can be chosen per state without
 *  two `text-…` utilities landing on the same element — see
 *  `paneToolbarToggleClass`. */
const paneToolbarShapeClass =
  "flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-[7px]";

export const paneToolbarInkClass =
  "text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)]";

/** The pressed / open state for a toolbar button that toggles something.
 *  Module-private on purpose: it is only ever correct when applied *instead of*
 *  the resting ink, which `paneToolbarToggleClass` guarantees and a caller
 *  composing it by hand does not (#11852). */
const paneToolbarActiveClass = "bg-[var(--fp-ink-wash)] text-[var(--fp-ink)]";

/** A control that is present but unavailable. Its own token because all three
 *  document-viewer buttons that can disable were hand-copying the same four
 *  utilities, hex included — the copy-paste the ink token exists to end. */
export const paneToolbarDisabledClass =
  "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-[var(--fp-ink-3)]";

export const artifactToolbarButtonClass = `${paneToolbarShapeClass} ${paneToolbarInkClass}`;

/** A toolbar button that toggles something, e.g. Details.
 *
 *  The two inks are mutually exclusive rather than layered. Composing
 *  `${artifactToolbarButtonClass} ${active ? paneToolbarActiveClass : ""}` puts
 *  two `text-…` utilities on the same element at equal specificity, so the
 *  generated stylesheet's source order decides — and the resting ink is
 *  emitted last, which silently swallowed the active ink and left the open
 *  state looking identical to the closed one (#11852). */
export const paneToolbarToggleClass = (active: boolean): string =>
  `${paneToolbarShapeClass} ${active ? paneToolbarActiveClass : paneToolbarInkClass}`;
